import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { VerifyEmailService } from 'src/app/services/verify-email-service/verify-email.api.service';
import { useModalManagerStore } from 'src/app/store/modal-manager.store';
import { usePhoneVerifyStore } from 'src/app/store/phone-verify.store';
import { useUserStore } from 'src/app/store/user.store';
import { UserProfileService } from 'src/app/user-profile/user-profile.service';
import { Subscription, take } from 'rxjs';

@Component({
  selector: 'app-verify-email-modal',
  template: `
    <app-modal [visible]="visible" [usePadding]="false">
      <div class="pin-verify-wrapper">
        <h5 class="pin-verify-header">Email verification</h5>
        <div class="flex__row--space-between user-phone-detail">
          <div class="flex__column sent-to-detail">
            @if (!editMode) {
              <div class="flex__column">
                <span class="sent-to-help-block s5">We sent a code to</span>
                <span class="s5">{{ email }}</span>
              </div>
            }
            @if (editMode) {
              <form
                [formGroup]="editEmailForm"
                class="edit-phone-form edit-email-form"
                >
                <input
                  class="form-control email-input"
                  formControlName="email"
                  (change)="onEmailChange($event)"
                  [value]="this.editEmailForm?.controls['email']?.value"
                  />
              </form>
            }
          </div>
          <div class="flex__center edit-actions-wrapper">
            <app-button
              [disabled]="editMode && !editEmailForm?.controls['email']?.valid"
              (click)="onEditClick()"
              class="{{ editMode ? 'primary' : 'dismiss' }}"
              label="{{ editMode ? 'Save' : 'Edit' }}"
            ></app-button>
          </div>
        </div>
        <div class="flex__column verify-phone-input-wrapper">
          <span class="flex__center s5 code-input-help-block"
            >Enter your code</span
            >
          <pin-mask-input
            (codeChanged)="onChange($event)"
            (codeCompleted)="onVerify()"
          ></pin-mask-input>
          <span class="flex__center s5 expires-in-help-block"
            >Expires in 5 minutes.</span
            >
        </div>
        <div class="flex__column action-wrapper">
          <button
            (click)="onResendCodeClick()"
            class="light flex__center"
            id="resend-code-btn"
            [disabled]="!allowResend"
            >
            Resend Code
          </button>
        </div>
      </div>
    </app-modal>
  `,
  styleUrls: ['./verify-email-modal.component.scss'],
  providers: [VerifyEmailService],
  standalone: false
})
export class VerifyEmailModalComponent implements OnInit, OnDestroy {
  visible: boolean;
  pinValue: string;
  value: string;
  loading: boolean;
  error: any;
  editMode = false;
  editEmailForm: FormGroup;
  allowResend = false;
  email: string;

  private modalManagerStore = useModalManagerStore.getState;

  private pinSubscription$: Subscription;
  private verifyEmailSubscription$: Subscription;

  constructor(
    private verifyEmailService: VerifyEmailService,
    private userService: UserProfileService
  ) { }

  toggleEditMode = () => (this.editMode = !this.editMode);

  onChange(event) {
    this.value = event;
  }

  onEmailChange(event) {
    this.editEmailForm.controls['email'].setValue(event.target.value);
  }

  initEditForm() {
    this.editEmailForm = new FormGroup({
      email: new FormControl(this.email, [
        Validators.required,
        Validators.email,
      ]),
    });
  }

  onSave() {
    const updatedEmail = this.editEmailForm?.controls['email'].value;
    if (this.email === updatedEmail) return;
    this.allowResend = true;
    this.email = updatedEmail;
    this.userService.updateUserProfile(this.editEmailForm.value);
  }

  onEditClick() {
    if (!this.editMode) {
      this.toggleEditMode();
      this.initEditForm();
    } else {
      this.onSave();
      this.toggleEditMode();
    }
  }

  onClose() {
    this.modalManagerStore().toggleModal('emailVerify', false);
  }

  onVerify() {
    this.pinSubscription$ = this.verifyEmailService
      .submitVerifyCode(this.value)
      .pipe(take(1))
      .subscribe();
  }

  onResendCodeClick() {
    const req$ = this.verifyEmailService.sendVerifyCode();
    this.verifyEmailSubscription$ = req$.pipe(take(1)).subscribe();
    this.allowResend = false;
  }

  ngOnInit(): void {
    useUserStore.subscribe((s) => {
      this.email = s?.email;
    });

    usePhoneVerifyStore.subscribe(
      (s) => s.submitPhoneVerifyPin,
      (newState) => {
        this.loading = newState.loading;
        this.error = newState.error;
      }
    );

    useModalManagerStore.subscribe(
      (s) => s.emailVerify,
      (newState) => {
        this.visible = newState;
      }
    );
  }

  ngOnDestroy() {
    if (this.pinSubscription$) {
      this.pinSubscription$.unsubscribe();
    }
    if (this.verifyEmailSubscription$) {
      this.verifyEmailSubscription$.unsubscribe();
    }
  }
}
